import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import PaymentForm, { BankTransferDetails } from './PaymentForm';
import { API_BASE_URL, authFetch } from '../../config/api.config';
import './Checkout.css';

interface BookingDetails {
  id: string;
  bookingNumber: string;
  eventDate: string;
  participants: number;
  totalAmount: number;
  depositAmount: number;
  paymentAmount: number; // Amount to pay now (deposit or full)
  isShortTerm: boolean;  // < 8 weeks = full payment required
  fullPaymentChosen: boolean; // T-020: Kunde hat Komplettzahlung gewaehlt (auch bei > 8 Wochen)
  status: string;
  customerName: string;
  customerEmail?: string;
  tripDetails?: {
    variant: string;
    tourTypeName: string;
    startTime: string;
    duration: string;
    includedMeals: string[];
  };
}

const Checkout: React.FC = () => {
  const { bookingId } = useParams<{ bookingId: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [booking, setBooking] = useState<BookingDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Get voucher data from URL params (passed from booking success) - will be overridden by API data
  const urlVoucherCode = searchParams.get('voucherCode');
  const urlVoucherDiscount = parseFloat(searchParams.get('voucherDiscount') || '0');

  // State for voucher data (from API or URL)
  const [voucherCode, setVoucherCode] = useState<string | null>(urlVoucherCode);
  const [voucherDiscountCents, setVoucherDiscountCents] = useState<number>(Math.round(urlVoucherDiscount * 100));

  useEffect(() => {
    fetchBookingDetails();
  }, [bookingId]);

  const fetchBookingDetails = async () => {
    try {
      setLoading(true);

      // T-009 (02.09.2026): authFetch statt rohem fetch, damit der 401-Refresh greift
      const response = await authFetch(`${API_BASE_URL}/bookings/${bookingId}`);

      if (response.ok) {
        const data = await response.json();
        console.log('Booking data received:', data);

        // Map backend response to expected format
        const subtotal = data.pricing?.totalGross || data.totalPrice || 0;

        // Get voucher data: prefer API data, fallback to URL params
        const apiVoucherCode = data.appliedVoucher?.code;
        const apiVoucherDiscount = data.appliedVoucher?.amountRedeemed || 0;
        const effectiveVoucherCode = apiVoucherCode || urlVoucherCode;
        const effectiveVoucherDiscount = apiVoucherDiscount > 0 ? apiVoucherDiscount : urlVoucherDiscount;

        // Update voucher state
        setVoucherCode(effectiveVoucherCode || null);
        setVoucherDiscountCents(Math.round(effectiveVoucherDiscount * 100));

        // Apply voucher discount if present
        const totalAmount = effectiveVoucherDiscount > 0 ? Math.max(0, subtotal - effectiveVoucherDiscount) : subtotal;
        const depositAmount = Math.round(totalAmount * 0.2 * 100); // 20% deposit in cents
        const totalAmountCents = Math.round(totalAmount * 100); // Convert to cents
        const subtotalCents = Math.round(subtotal * 100);

        // Get customer name from nested customer object or user
        const customerName = data.customer
          ? `${data.customer.firstName || ''} ${data.customer.lastName || ''}`.trim()
          : data.user
            ? `${data.user.firstName || ''} ${data.user.lastName || ''}`.trim()
            : 'Kunde';

        // Get event date from schedule
        const eventDate = data.scheduleDate || data.schedule?.date || new Date().toISOString();

        // Get tour type name from various possible sources
        const tourTypeName = data.tourTypeName
          || data.calendarSlot?.tourType
          || data.schedule?.tourType
          || '';

        // Check if booking is short-term (< 8 weeks until event)
        // Use backend flag if available, otherwise calculate
        let isShortTerm = data.isShortTermBooking ?? false;
        if (!data.isShortTermBooking) {
          const eventDateObj = new Date(eventDate);
          const now = new Date();
          const daysUntilEvent = Math.floor((eventDateObj.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
          const weeksUntilEvent = Math.floor(daysUntilEvent / 7);
          isShortTerm = weeksUntilEvent <= 8;
        }

        // T-020: gewaehlte Komplettzahlung fuehrt ebenfalls zur Vollzahlung (Backend setzt dann
        // auch IsShortTermBooking ueber die Kurzfristige Rechnung, hier zur Sicherheit explizit)
        const fullPaymentChosen = data.fullPaymentChosen === true;
        if (fullPaymentChosen) {
          isShortTerm = true;
        }

        // Payment amount: full amount for short-term, deposit for regular
        const paymentAmount = isShortTerm ? totalAmountCents : depositAmount;

        console.log('Booking payment calculation:', {
          totalAmount,
          totalAmountCents,
          depositAmount,
          isShortTerm,
          paymentAmount,
          eventDate
        });

        setBooking({
          id: data.id?.toString() || bookingId || '',
          bookingNumber: data.bookingNumber || `B-${data.id}`,
          eventDate: eventDate,
          participants: data.participantCount || 0,
          totalAmount: totalAmountCents,
          depositAmount: depositAmount,
          paymentAmount: paymentAmount,
          isShortTerm: isShortTerm,
          fullPaymentChosen: fullPaymentChosen,
          status: data.status || 'Confirmed',
          customerName: customerName,
          customerEmail: data.customer?.email || data.user?.email,
          tripDetails: {
            variant: data.variant || 'Standard',
            tourTypeName: tourTypeName,
            startTime: formatTimeSpan(data.startTime),
            duration: '3-4 Stunden',
            includedMeals: []
          }
        });
      } else {
        throw new Error('Failed to fetch booking details');
      }
    } catch (err) {
      setError('Fehler beim Laden der Buchungsdetails');
      console.error('Error fetching booking:', err);
    } finally {
      setLoading(false);
    }
  };

  const handlePaymentSuccess = (paymentMethod: string, amount: number, details?: BankTransferDetails) => {
    navigate('/payment/success', {
      state: {
        bookingNumber: booking?.bookingNumber,
        amount: amount / 100, // Convert from cents to euros
        paymentMethod: paymentMethod,
        // T-021: Ueberweisungsdaten (Rechnungsnummer, IBAN, BIC) fuer die Erfolgsseite
        bankTransfer: details
      }
    });
  };

  // Format currency for German locale (e.g., 1.234,56 €)
  const formatCurrency = (amountInCents: number) => {
    return new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: 'EUR'
    }).format(amountInCents / 100);
  };

  // Format TimeSpan from backend (e.g., "11:00:00" -> "11:00 Uhr")
  const formatTimeSpan = (timeSpan: string | undefined): string => {
    if (!timeSpan) return '-';
    // Handle "HH:mm:ss" format
    const parts = timeSpan.split(':');
    if (parts.length >= 2) {
      return `${parts[0]}:${parts[1]} Uhr`;
    }
    return timeSpan;
  };

  if (loading) {
    return (
      <div className="checkout-loading">
        <div className="spinner" style={{ width: '48px', height: '48px' }}></div>
      </div>
    );
  }

  if (error || !booking) {
    return (
      <div className="checkout-error">
        <h2>Fehler</h2>
        <p>{error || 'Buchung nicht gefunden'}</p>
        <button onClick={() => navigate('/')}>Zurück zur Startseite</button>
      </div>
    );
  }

  return (
    <div className="checkout-container">
      <div className="checkout-header">
        <h1>Zahlung abschließen</h1>
        <p className="booking-number">Buchung {booking.bookingNumber}</p>
      </div>

      <div className="checkout-content">
        <div className="booking-summary">
          <h2>Buchungsübersicht</h2>
          
          <div className="summary-card">
            <div className="summary-details">
              <div className="detail-row">
                <span className="label">Kunde:</span>
                <span className="value">{booking.customerName}</span>
              </div>
              <div className="detail-row">
                <span className="label">Veranstaltungsdatum:</span>
                <span className="value">
                  {new Date(booking.eventDate).toLocaleDateString('de-DE', {
                    weekday: 'long',
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })}
                </span>
              </div>
              <div className="detail-row">
                <span className="label">Teilnehmer:</span>
                <span className="value">{booking.participants} Personen</span>
              </div>
              
              {booking.tripDetails && (
                <>
                  <div className="detail-row">
                    <span className="label">Tour-Typ:</span>
                    <span className="value">{booking.tripDetails.tourTypeName || booking.tripDetails.variant}</span>
                  </div>
                  <div className="detail-row">
                    <span className="label">Startzeit:</span>
                    <span className="value">{booking.tripDetails.startTime || '-'}</span>
                  </div>
                </>
              )}
            </div>

            <div className="price-summary">
              {voucherDiscountCents > 0 && (
                <>
                  <div className="price-row">
                    <span>Zwischensumme:</span>
                    <span>{formatCurrency(booking.totalAmount + voucherDiscountCents)}</span>
                  </div>
                  <div className="price-row voucher-row">
                    <span>Gutschein ({voucherCode}):</span>
                    <span className="voucher-discount">-{formatCurrency(voucherDiscountCents)}</span>
                  </div>
                </>
              )}
              <div className="price-row">
                <span>Gesamtbetrag:</span>
                <span className="total-amount">{formatCurrency(booking.totalAmount)}</span>
              </div>

              {booking.isShortTerm ? (
                <>
                  <div className="price-row highlight full-payment">
                    <span>{booking.fullPaymentChosen ? 'Komplettzahlung:' : 'Vollzahlung:'}</span>
                    <span className="deposit-amount">{formatCurrency(booking.paymentAmount)}</span>
                  </div>
                  {booking.fullPaymentChosen ? (
                    <div className="payment-note">
                      💡 Sie haben die Komplettzahlung gewählt, der Gesamtbetrag wird jetzt fällig.
                    </div>
                  ) : (
                    <div className="payment-note short-term-notice">
                      ⚠️ Bei kurzfristigen Buchungen (weniger als 8 Wochen vor der Fahrt) ist die
                      <strong> vollständige Zahlung</strong> erforderlich.
                    </div>
                  )}
                </>
              ) : (
                <>
                  <div className="price-row highlight">
                    <span>Anzahlung (20%):</span>
                    <span className="deposit-amount">{formatCurrency(booking.depositAmount)}</span>
                  </div>
                  <div className="payment-note">
                    💡 Die Restzahlung von {formatCurrency(booking.totalAmount - booking.depositAmount)} ist
                    25 Tage vor der Floßfahrt fällig.
                  </div>
                </>
              )}
            </div>
          </div>
        </div>

        <div className="payment-section">
          <PaymentForm
            bookingId={parseInt(booking.id)}
            bookingNumber={booking.bookingNumber}
            amount={booking.paymentAmount}
            onSuccess={handlePaymentSuccess}
            onCancel={() => navigate('/dashboard')}
          />
        </div>
      </div>
    </div>
  );
};

export default Checkout;
